<script lang="ts" setup>
import {ref, computed} from 'vue'
import type {Tag} from '@backend/types'
import {Badge} from '@/components/ui/badge'
import {Button} from '@/components/ui/button'
import {Popover, PopoverContent, PopoverTrigger} from '@/components/ui/popover'
import {Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList} from '@/components/ui/command'
import {Check, ChevronsUpDown} from 'lucide-vue-next'
import {cn} from '@/lib/utils'
import i18next from 'i18next'

const props = defineProps<{
  modelValue: string[]
  tags: Tag[]
}>()

const emit = defineEmits<{
  'update:modelValue': [value: string[]]
}>()

const t = i18next.t.bind(i18next)
const popoverOpen = ref(false)

const labelTags = computed(() => props.tags.filter((tag) => tag.type === 'Labels'))
const areaTags = computed(() => props.tags.filter((tag) => tag.type === 'Areas'))

function isSelected(tagName: string) {
  return props.modelValue.includes(tagName)
}

function toggleTag(tagName: string) {
  if (isSelected(tagName)) {
    emit(
      'update:modelValue',
      props.modelValue.filter((t) => t !== tagName)
    )
  } else {
    emit('update:modelValue', [...props.modelValue, tagName])
  }
}
</script>

<template>
  <Popover v-model:open="popoverOpen">
    <PopoverTrigger as-child>
      <Button variant="outline" class="h-auto min-h-9 w-full justify-between font-normal">
        <div class="flex flex-wrap gap-1">
          <Badge v-for="tag in modelValue" :key="tag" variant="secondary" class="text-xs"> #{{ tag }} </Badge>
          <span v-if="modelValue.length === 0" class="text-muted-foreground">{{ t('fields.tags') }}</span>
        </div>
        <ChevronsUpDown class="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </Button>
    </PopoverTrigger>
    <PopoverContent class="w-full p-0" align="start">
      <Command multiple>
        <CommandInput :placeholder="t('fields.tags')" />
        <CommandList>
          <CommandEmpty>{{ t('messages.no_tasks') }}</CommandEmpty>
          <CommandGroup v-if="labelTags.length" heading="Labels">
            <CommandItem v-for="tag in labelTags" :key="tag.tag" :value="tag.tag" @select="toggleTag(tag.tag)">
              <Check :class="cn('mr-2 h-4 w-4', isSelected(tag.tag) ? 'opacity-100' : 'opacity-0')" />
              {{ tag.tag }}
            </CommandItem>
          </CommandGroup>
          <CommandGroup v-if="areaTags.length" heading="Areas">
            <CommandItem v-for="tag in areaTags" :key="tag.tag" :value="tag.tag" @select="toggleTag(tag.tag)">
              <Check :class="cn('mr-2 h-4 w-4', isSelected(tag.tag) ? 'opacity-100' : 'opacity-0')" />
              {{ tag.tag }}
            </CommandItem>
          </CommandGroup>
        </CommandList>
      </Command>
    </PopoverContent>
  </Popover>
</template>
